"use client";

import { ReactNode, Suspense, useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { useSession } from "next-auth/react";
import { Crisp } from "crisp-sdk-web";
import { SessionProvider } from "next-auth/react";
import NextTopLoader from "nextjs-toploader";
import { Toaster } from "react-hot-toast";
import { Tooltip } from "react-tooltip";
import config from "@/config";
import { CreditsProvider } from "@/contexts/CreditsContext";
import { AuthModalProvider } from "@/contexts/AuthModalContext";
import { CheckoutLoadingProvider } from "@/contexts/CheckoutLoadingContext";
import { PricingModalProvider } from "@/contexts/PricingModalContext";
import { RedirectLoadingProvider } from "@/contexts/RedirectLoadingContext";
import PaymentResultModal from "@/components/PaymentResultModal";
import PendingCheckoutResume from "@/components/PendingCheckoutResume";
import PostAuthRedirectGuard from "@/components/PostAuthRedirectGuard";
import { pageview, setUserId, restoreAndCleanupRedirectSession, trackFunnelEvent, storeSessionForRedirect } from "../libs/ga";
import HalloweenBanner from "@/components/HalloweenBanner";

// Crisp customer chat support:
// This component is separated from ClientLayout because it needs to be wrapped with <SessionProvider> to use useSession() hook
const CrispChat = (): null => {
  const pathname = usePathname();
  const { data } = useSession();

  useEffect(() => {
    if (config?.crisp?.id) {
      // Set up Crisp
      Crisp.configure(config.crisp.id);

      // (Optional) If onlyShowOnRoutes array is not empty in config.js file, Crisp will be hidden on the routes in the array.
      // Use <AppButtonSupport> instead to show it (user clicks on the button to show Crisp-it cleans the UI)
      if (
        config.crisp.onlyShowOnRoutes &&
        !config.crisp.onlyShowOnRoutes?.includes(pathname)
      ) {
        Crisp.chat.hide();
        Crisp.chat.onChatClosed(() => {
          Crisp.chat.hide();
        });
      }
    }
  }, [pathname]);

  // Add User Unique ID to Crisp to easily identify users when reaching support (optional)
  useEffect(() => {
    if (data?.user && config?.crisp?.id) {
      Crisp.session.setData({ userId: data.user?.id });
    }
  }, [data]);

  return null;
};

// Separate component for GA user tracking
const GAUserTracking = (): null => {
  const { data } = useSession();

  // Set GA user_id when available to stitch sessions across domains
  useEffect(() => {
    if (data?.user?.id) {
      setUserId(data.user.id);

      // Only track authentication ONCE per session (not on every page load)
      const authTracked = sessionStorage.getItem('pfp_auth_tracked');
      if (!authTracked) {
        trackFunnelEvent('acquisition', 'authenticated', {
          user_id: data.user.id,
          auth_method: 'google' // Could be enhanced to detect actual method
        });
        sessionStorage.setItem('pfp_auth_tracked', 'true');
      }
    }
  }, [data?.user?.id]);

  return null;
};

// All the client wrappers are here (they can't be in server components)
// 1. SessionProvider: Allow the useSession from next-auth (find out if user is auth or not)
// 2. NextTopLoader: Show a progress bar at the top when navigating between pages
// 3. Toaster: Show Success/Error messages anywhere from the app with toast()
// 4. Tooltip: Show tooltips if any JSX elements has these 2 attributes: data-tooltip-id="tooltip" data-tooltip-content=""
// 5. CrispChat: Set Crisp customer chat support (see above)
// 6. GAUserTracking: Handle Google Analytics user tracking and session restoration
const ClientLayout = ({ children }: { children: ReactNode }) => {
  const pathname = usePathname();
  const sessionRestoredRef = useRef(false);
  const isOAuthPopup = pathname?.startsWith("/auth/popup");

  // Check for session restoration from OAuth/Stripe redirects ONCE on mount
  useEffect(() => {
    if (isOAuthPopup) return;
    if (sessionRestoredRef.current) return;
    sessionRestoredRef.current = true;

    const restored = restoreAndCleanupRedirectSession();
    if (restored && restored.redirectType) {
      const timeSinceRedirect = Date.now() - restored.timestamp;

      // Restore session ID if it was stored
      if (restored.sessionId && typeof window !== "undefined") {
        sessionStorage.setItem('pfp_session_id', restored.sessionId);
      }

      // Track successful session restoration after external redirect
      trackFunnelEvent('acquisition', 'session_restored', {
        redirect_type: restored.redirectType,
        time_away: timeSinceRedirect,
        original_source: restored.source
      });
    }
  }, [isOAuthPopup]);

  // Send GA page_view on route changes
  useEffect(() => {
    if (isOAuthPopup) return;
    if (typeof window !== "undefined") {
      pageview(window.location.pathname + window.location.search);
    }
  }, [pathname, isOAuthPopup]);

  // OAuth popup windows must stay minimal: only SessionProvider. Mounting the
  // full app shell (redirect overlay, checkout resume, auth modal, etc.)
  // interferes with the popup's signIn redirect to Google.
  if (isOAuthPopup) {
    return <SessionProvider>{children}</SessionProvider>;
  }

  return (
    <>
      <SessionProvider>
        <RedirectLoadingProvider>
        {/* Show a progress bar at the top when navigating between pages */}
        <NextTopLoader color={config.colors.main} showSpinner={false} />

        {/* Halloween Sitewide Banner */}
        <HalloweenBanner />

        {/* CreditsProvider manages credits/subscription state globally.
            AuthModalProvider mounts the global login modal (light theme, dark
            overlay) and exposes `useAuthModal()` for any component to trigger
            it - Google sign-in completes in a popup, so the host page never
            reloads. */}
        <CreditsProvider>
          <CheckoutLoadingProvider>
          <AuthModalProvider>
            <PricingModalProvider>
              {children}
              {/* Payment result modal is mounted globally so any page that
                  receives ?payment_success=... in the URL (as configured in
                  config.payments.successUrl/cancelUrl) can render it. */}
              <Suspense fallback={null}>
                <PaymentResultModal />
              </Suspense>
              <PendingCheckoutResume />
              <PostAuthRedirectGuard />
            </PricingModalProvider>
          </AuthModalProvider>
          </CheckoutLoadingProvider>
        </CreditsProvider>

        {/* Show Success/Error messages anywhere from the app with toast() */}
        <Toaster
          position="top-center"
          containerStyle={{
            top: 16,
            bottom: "auto",
            left: 0,
            right: 0,
          }}
          toastOptions={{
            duration: 3000,
          }}
        />

        {/* Show tooltips if any JSX elements has these 2 attributes: data-tooltip-id="tooltip" data-tooltip-content="" */}
        <Tooltip
          id="tooltip"
          className="z-[60] !opacity-100 max-w-sm shadow-lg"
        />

        {/* Google Analytics user tracking - separate from Crisp */}
        <GAUserTracking />

        {/* Set Crisp customer chat support */}
        <CrispChat />
        </RedirectLoadingProvider>
      </SessionProvider>
    </>
  );
};

export default ClientLayout;
